/**
 * Central Business Profile Pre-fill Hook (Read-Only)
 *
 * Minimal integration with Central Business Profile API.
 * Only READS from central - all saves go to local storage.
 *
 * Use case: Pre-fill business info for users who already have a profile
 * from another FutureSense app (Invoicer, AI Caller, etc.)
 *
 * @see /home/mainubu-home/.local/share/claude/skills/migrate-to-central-business-profile.md
 */

import { useState, useEffect, useCallback } from 'react';
// ⚠️ IMPORTANT: Booker uses named Firebase apps, so we import auth directly
import { auth } from '../firebase/config';

const CENTRAL_API_URL = process.env.REACT_APP_CENTRAL_API_URL
  || 'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api';

/**
 * Read-only hook to fetch central business profile for pre-filling forms.
 * Does NOT save to central - all saves remain local.
 *
 * @param {Object} options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} { profile, loading, error, refetch }
 */
export function useCentralProfilePrefill(options = {}) {
  const { autoFetch = true } = options;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const fetchProfile = useCallback(async () => {
    // Guard: don't fetch if no user
    if (!auth.currentUser) {
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CENTRAL_API_URL}/business-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // 404 = no profile yet (normal for new users)
        if (response.status === 404) {
          setProfile(null);
          setFetched(true);
          return null;
        }
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data = await response.json();
      const centralProfile = data.profile || null;
      setProfile(centralProfile);
      setFetched(true);
      return centralProfile;
    } catch (err) {
      // Don't treat "not found" as error
      if (err.message.includes('404') || err.message.includes('not found')) {
        setProfile(null);
        setFetched(true);
        return null;
      }
      console.warn('[CentralProfile] Fetch failed (non-critical):', err.message);
      setError(err.message);
      setFetched(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount when auth is ready
  useEffect(() => {
    if (!autoFetch) return;

    // If already authenticated, fetch immediately
    if (auth.currentUser) {
      fetchProfile();
      return;
    }

    // Wait for auth to be ready
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !fetched) {
        fetchProfile();
      } else if (!user) {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [autoFetch, fetchProfile, fetched]);

  return {
    profile,
    loading,
    error,
    fetched,
    refetch: fetchProfile
  };
}

/**
 * Helper to map central profile fields to Booker's field names.
 * Handles field name differences between central schema and Booker schema.
 *
 * @param {Object} centralProfile - Profile from central API
 * @returns {Object} - Mapped fields for Booker forms
 */
export function mapCentralToBooker(centralProfile) {
  if (!centralProfile) return null;

  return {
    // Direct mappings
    businessName: centralProfile.businessName || '',
    businessPhone: centralProfile.businessPhone || '',
    businessEmail: centralProfile.businessEmail || '',
    tagline: centralProfile.tagline || '',

    // Field name differences
    businessDescription: centralProfile.description || '',

    // Address mapping (central uses "address", Booker uses "businessAddress")
    businessAddress: centralProfile.address ? {
      street: centralProfile.address.street || '',
      city: centralProfile.address.city || '',
      state: centralProfile.address.state || '',
      postalCode: centralProfile.address.postalCode || '',
      country: centralProfile.address.country || ''
    } : null,

    // Branding
    logoUrl: centralProfile.branding?.logoUrl || '',
    primaryColor: centralProfile.branding?.primaryColor || '',
    secondaryColor: centralProfile.branding?.secondaryColor || '',

    // Additional fields that might exist
    industry: centralProfile.industry || '',
    timezone: centralProfile.timezone || '',

    // These don't exist in central but Booker uses them
    // Returning null so we know they weren't pre-filled
    ownerName: null,
    foundedYear: null,
    companyStory: null,
    mission: null,
    vision: null,
    socialMedia: null,
    businessSlug: null
  };
}

export default useCentralProfilePrefill;
